import React from 'react';

import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';

interface AutoSaveIndicatorProps {
  isSaving: boolean;
  lastSaved: Date | null;
  /**
   * Optional server-owned save receipt for surfaces that persist revisions
   * rather than timestamps. When present it becomes the tooltip without
   * inventing a client-side save time.
   */
  savedDetail?: string;
  className?: string;
}

export const AutoSaveIndicator: React.FC<AutoSaveIndicatorProps> = ({
  isSaving,
  lastSaved,
  savedDetail,
  className,
}) => {
  if (!isSaving && !lastSaved && !savedDetail) {
    return null;
  }

  const savedAt = lastSaved
    ? new Date(lastSaved).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : null;

  return (
    // `text-detail font-medium`, not `text-xs`: this sits in a header row
    // beside `StatusBadge` and `QualityChip`, both of which are 11px/500. At
    // 12px/400 it was a fourth type treatment in a bar that should have two,
    // and the odd one out was the quietest thing next to the loudest.
    <div
      className={cn(
        'flex items-center gap-1.5 text-detail font-medium text-muted-foreground/60',
        className,
      )}
      // The exact minute moves into the title. It is worth having when you are
      // wondering whether a save landed; it is not worth a permanent slot in
      // the busiest row in the app. Founder, 2026-08-21, listing what crowds
      // that header: "the 'Saved' says 'Saved 11:10'".
      title={savedDetail ?? (savedAt ? `Last saved at ${savedAt}` : undefined)}
    >
      {isSaving ? (
        <>
          <Spinner size="sm" className="size-2" />
          <span>Saving</span>
        </>
      ) : lastSaved || savedDetail ? (
        <>
          <div className="size-1.5 rounded-full bg-success" />
          <span>Saved</span>
        </>
      ) : null}
    </div>
  );
};
